import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { DragIndicator } from '@material-ui/icons';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axiosUI from '../../axiosUI';
import Drawer from '../Drawer/Drawer';
import Button from '../Button/Button';
import Loader from '../Loader/Loader';
import { withGlobalContext } from '../../App';
import {
  NOTIFICATIONS,
  RESOURCE_STATES,
  CHANNEL_VARIANTS,
} from '../../constants';

class ChannelOrder extends Component {
  static propTypes = {
    dissmissDrawer: PropTypes.func.isRequired,
    country: PropTypes.string,
    language: PropTypes.string,
    createNotification: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      channels: [],
      loading: false,
    };
  }

  componentDidMount = async () => {
    try {
      await this.fetchChannelsAndInitializeState();
    } catch (error) {
      console.log(error);
      this.props.createNotification({
        message: `Failed to fetch channels`,
        type: NOTIFICATIONS.TYPE.ERROR,
        timeout: 3000,
      });
    }
  };

  onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    const channels = this.state.channels.slice();
    const oldIndex = result.source.index;
    const newIndex = result.destination.index;

    // remove item from old index
    const [removed] = channels.splice(oldIndex, 1);

    // insert removed item at new index
    channels.splice(newIndex, 0, removed);

    this.setState({ channels });
  };

  fetchChannelsAndInitializeState = async () => {
    const channelUrl = `/api/channels`;

    let { data } = await axiosUI.get(channelUrl, {
      params: {
        salesChannel: this.props.country,
        locale: this.props.language,
        state: RESOURCE_STATES.PUBLISHED,
      },
    });

    // put all unsorted elements at last
    data.sort((a, b) => {
      if (a.sorting_position === undefined) {
        return 1;
      }
      if (b.sorting_position === undefined) {
        return -1;
      }
      return a.sorting_position - b.sorting_position;
    });

    // assign sorting_position if undefined
    data.forEach((channel, index) => {
      if (channel.sorting_position === undefined) {
        channel.sorting_position = index;
      }
    });

    this.setState({
      channels: data,
    });
  };

  saveOrder = () => {
    this.setState({ loading: true }, async () => {
      try {
        const channelsWithUpdatedOrder = this.state.channels.map(
          (channel, index) => {
            return {
              ...channel,
              sorting_position: index,
            };
          }
        );

        const url = `/api/channels/ordering`;
        await axiosUI.post(url, channelsWithUpdatedOrder);
        await this.fetchChannelsAndInitializeState();

        this.props.createNotification({
          message: `Channel order is saved`,
          type: NOTIFICATIONS.TYPE.SUCCESS,
          timeout: 5000,
        });
      } catch (error) {
        console.log(error);

        this.props.createNotification({
          message: `Failed to save changes`,
          type: NOTIFICATIONS.TYPE.ERROR,
          timeout: 3000,
        });
      }

      this.setState({ loading: false });
    });
  };

  getUniqueKey = channel => {
    return `${channel.id}_${channel.variant}`;
  };

  render() {
    return (
      <Drawer
        title="Define Channel Order (for published variants)"
        size="small"
        onDismiss={this.state.loading ? undefined : this.props.dissmissDrawer}>
        <Loader loading={this.state.loading}>
          <div className="flex flex-col justify-between h-full">
            <DragDropContext onDragEnd={this.onDragEnd}>
              <Droppable droppableId="channelList">
                {provided => (
                  <ul
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="list-reset m-0">
                    {this.state.channels.map((channel, index) => {
                      const key = this.getUniqueKey(channel);
                      return (
                        <Draggable
                          key={key}
                          draggableId={key}
                          index={index}>
                          {(provided, snapshot) => (
                            <li
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={cx(
                                'flex items-center p-2 rounded bg-almostWhite mb-4',
                                {
                                  'shadow-lg': snapshot.isDragging,
                                }
                              )}>
                              <Button
                                {...provided.dragHandleProps}
                                type="transparent"
                                className="p-0-important shadow-none-important h-6 mr-8">
                                <DragIndicator
                                  className="text-blue"
                                  titleAccess="Reorder Channels"
                                />
                              </Button>
                              <div className="w-full flex">
                                <span className="text-lg font-medium">{channel.name}</span>
                                <span className="ml-2">
                                  (
                                  {channel.variant === CHANNEL_VARIANTS.OFFICIAL
                                    ? 'Default config'
                                    : 'Override Config'}
                                  )
                                </span>
                              </div>
                            </li>
                          )}
                        </Draggable>
                      );
                    })}
                  </ul>
                )}
              </Droppable>
            </DragDropContext>
            <div>
              <Button
                type="green"
                className="p-2 w-48 float-right"
                onClick={this.saveOrder}>
                Save Order
              </Button>
            </div>
          </div>
        </Loader>
      </Drawer>
    );
  }
}

export default  withGlobalContext(ChannelOrder);
